import { Component, HostListener, ViewChild } from '@angular/core';
import Quill from 'quill';
import { UtilService } from './services/util.service';
import { DomSanitizer } from '@angular/platform-browser';
import {
  Router,
  ActivatedRoute,
  NavigationEnd,
  NavigationError,
  NavigationStart,
} from '@angular/router';
import { LayoutFile } from './models/settings.model';
import { LayoutService } from './services/layout.service';
import { SchoolmanagementService } from './services/schoolmanagement.service';
import {
  FeatureGroups,
  SchoolConfig,
  SchoolInfoBundle,
} from './models/school/schooladminitem.model';
import { DetectlocationService } from './services/detectlocation.service';
import { SwUpdate } from '@angular/service-worker';
import { OfflineDbService } from './services/offline/offline-db.service';
import { OfflineCryptoService } from './services/offline/offline-crypto.service';
import { OfflineConnectivityService, ConnectivityState } from './services/offline/offline-connectivity.service';
import { OfflineSessionService } from './services/offline/offline-session.service';
import { isOfflineModeEnabled } from './services/offline/offline-feature-gate';
import { UseraccessserviceService } from './services/useraccessservice.service';
import { OfflineEntitlementService } from './services/offline/offline-entitlement.service';
import { ASSESSMENT_ROUTE_PATHS } from './services/offline/offline-block-assessment.guard';
import { getCurrentSchoolSubdomain, getCurrentUserId } from './services/offline/offline-cache-key';
import { OfflineBootstrapCacheService } from './services/offline/offline-bootstrap-cache.service';
import { isNetworkFailure, knownOffline, withTimeout } from './services/offline/offline-network-failure';
import { OfflineVideoPlaybackService } from './services/offline/offline-video-playback.service';
import { offlineVideoSupported } from './services/offline/offline-video-capability';

// Long enough that a merely slow connection still wins, short enough that a student on a dead
// one is not staring at a blank screen. The cached fallback behind it is instant.
const BOOTSTRAP_TIMEOUT_MS = 8000;
//import { FeatureGroups } from './models/billing/BillingAccountInfo';
const BlockEmbed = Quill.import('blots/block/embed');

declare const $: any;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent {
  title = 'impataapp';

  layoutFiles: LayoutFile[] = [];
  activeLayoutFiles: LayoutFile[] = [];
  private onInitDone = false;
  isOnline: boolean;
  isSchoolEnabled: boolean | null = null;
  isNotFoundError: boolean;
  startupFailed = false;
  logoUrl = '';

  // Offline mode (docs/offline-mode-plan.md P2). offlineFeatureOn covers only the plan-level
  // flag + school toggle; isAdmin is fetched from GET /offline/session alongside it, and only
  // when offlineFeatureOn is already true (no point calling that endpoint otherwise). With the
  // flag off, connState still exists but offlineBannerMode is always false, so the *ngIf
  // expressions below reduce to exactly what they were before this feature existed.
  connState: ConnectivityState = 'online';
  offlineFeatureOn = false;
  offlineIsAdmin = false;
  // Evidence from the DEVICE rather than from the server flag: does this student actually have
  // anything downloaded here? If they do, offline mode is plainly on for them — they used it —
  // so a session lookup that could not be resolved offline must not downgrade them to the
  // admin wall. Filled in asynchronously, never awaited during startup.
  hasSavedContentOnDevice = false;
  get offlineBannerMode(): boolean {
    return (this.offlineFeatureOn || this.hasSavedContentOnDevice) && !this.offlineIsAdmin;
  }
  // Whether THIS school has offline saving turned on at all, independent of offlineIsAdmin —
  // used by the plain connectivity wall (shown to admins and to every school with the feature
  // off) to decide whether a "view downloaded lessons" link means anything. A school with the
  // feature off has no hub to send anyone to.
  get offlineFeatureAvailable(): boolean {
    return this.offlineFeatureOn || this.hasSavedContentOnDevice;
  }
  // Tracks whether the current route is one of ASSESSMENT_ROUTE_PATHS — updated on every
  // NavigationEnd. Independent of offlineFeatureOn/admin status: assessments are blocked
  // offline for everyone (D3), not gated behind the offline-content-saving feature flag.
  isOnAssessmentRoute = false;
  get assessmentOfflineBlocked(): boolean {
    return this.isOnAssessmentRoute && this.connState !== 'online';
  }

  // Offline mode's blocking overlay — confines all offline browsing to the downloads hub
  // instead of trying to keep arbitrary pages working offline. Fires only on a genuine
  // 'offline' state, not 'degraded' — a flaky-but-technically-connected student just sees
  // ordinary request errors, same as anyone else on a bad connection.
  get offlineBlocked(): boolean {
    return this.offlineBannerMode && this.connState === 'offline';
  }
  // The exceptions to the block above — the hub itself, its read-only lesson viewer, and the
  // video player must stay reachable while blocked or there would be no way to reach the
  // content the overlay says to go read. /media is included because a saved lesson's note
  // HTML embeds its video as a same-app <iframe src="/media?..."> (Quill's MediaBlot) — that
  // iframe boots a whole separate instance of this app, with its own router evaluating this
  // same getter against its own /media URL, so without this it would show its own copy of
  // this exact overlay instead of the downloaded video. Same router.url-checking pattern as
  // isOnAssessmentRoute above.
  get offlineHubRouteAllowed(): boolean {
    return (
      this.router.url.startsWith('/user/managedownloads') ||
      this.router.url.startsWith('/user/downloadedlesson') ||
      this.router.url.startsWith('/media')
    );
  }

  constructor(
    public utilservice: UtilService,
    private router: Router,
    public schoolservice: SchoolmanagementService,
    private layoutService: LayoutService,
    private sanitizer: DomSanitizer,
    private locationService: DetectlocationService,
    private offlineConnectivity: OfflineConnectivityService,
    private offlineSession: OfflineSessionService,
    private useraccessservice: UseraccessserviceService,
    private offlineEntitlement: OfflineEntitlementService,
    private offlineBootstrapCache: OfflineBootstrapCacheService,
    private offlineVideoPlayback: OfflineVideoPlaybackService,
    private swUpdate: SwUpdate,
    private offlineDb: OfflineDbService,
    private offlineCrypto: OfflineCryptoService
  ) {
    MediaBlot.blotName = 'media';
    MediaBlot.tagName = 'iframe';
    Quill.register(MediaBlot);

    this.hideContent();
    this.router.events.subscribe((event: any) => {
      this.routerEventHandler(event);
    });
  }

  async preloadCustomStyles() {
    const school: any = await this.schoolservice.getSchoolFromSubdomain();

    this.layoutFiles = await this.layoutService.getLayoutFiles(school.id);
    for (const layoutFile of this.layoutFiles) {
      layoutFile.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        layoutFile.fileurl
      );
    }
  }

  // Guarded on loggedIn() — GET /offline/session is [Authorize]'d, and NotificationInterceptor
  // treats any 401 as "Authentication failed" + redirects to /logout (:42-44 in
  // notification.interceptor.ts). Calling this unconditionally would log out anonymous
  // visitors on marketing/login pages the moment this ran. Offline mode is available to every
  // school regardless of plan tier (explicit user direction) — the only gate is the
  // per-school toggle this call itself fetches, so every logged-in visit checks it.
  // Falls back to a locally-persisted copy on a network failure (status 0) — without this, a
  // page reload while offline fails on this very first request and the catch block below
  // sets startupFailed, blocking the whole app (including the router) before the user's
  // already-saved lesson content ever gets a chance to load from its own cache.
  private async loadSchoolInfoBundle(): Promise<SchoolInfoBundle> {
    // Nothing used to ask whether a network existed before firing this. Going offline did not
    // stop the call, it only guaranteed the call failed first - and on a dying connection that
    // meant waiting out a gateway timeout before any cached copy was even looked at.
    if (knownOffline()) {
      const cached = await this.offlineBootstrapCache.getCached<SchoolInfoBundle>('schoolconfig');
      if (cached) {
        this.utilservice.schoolInfoBundlePromise = Promise.resolve(cached);
        return cached;
      }
      // No cached copy: fall through and let the request fail honestly rather than inventing
      // a reason the app cannot start.
    }

    try {
      const bundle = await withTimeout(this.utilservice.getStaticSchoolInfoBundle(), BOOTSTRAP_TIMEOUT_MS, 'School/getschoolconfig');
      this.offlineBootstrapCache.cache('schoolconfig', bundle).catch((err) => console.error('[offline] failed to cache school bundle', err));
      return bundle;
    } catch (error) {
      if (isNetworkFailure(error)) {
        const cached = await this.offlineBootstrapCache.getCached<SchoolInfoBundle>('schoolconfig');
        if (cached) {
          // Also covers the timeout case: the underlying request is still in flight and still
          // memoized, so it has to be replaced here or every later caller awaits the stall we
          // just walked away from.
          // UtilService memoizes the rejected fetch internally (schoolInfoBundlePromise) —
          // without resetting it here, every later hasFeature()/getStaticSchoolInfoBundle()
          // call anywhere in the app this session would re-throw the same failure instead of
          // seeing this fallback. Same pattern as writing schoolInfoBundle directly below.
          this.utilservice.schoolInfoBundlePromise = Promise.resolve(cached);
          return cached;
        }
      }
      throw error;
    }
  }

  private async initOfflineSession(): Promise<void> {
    try {
      if (!this.useraccessservice.loggedIn()) return;
      const session = await this.offlineSession.refresh();
      this.offlineFeatureOn = isOfflineModeEnabled(session.offlineEnabled);
      this.offlineIsAdmin = session.isAdmin;
      if (this.offlineFeatureOn && !this.offlineIsAdmin) {
        // Install the offline video worker NOW, while the app has just proved it has a
        // network. navigator.serviceWorker.register() fetches the script over the network, so
        // it cannot succeed offline - and until this line the only thing that ever registered
        // it was media.component.ts, meaning the worker could first be needed on a page the
        // student opens offline, by which point installing it is already impossible. Cheap and
        // idempotent: an existing registration just gets an update check.
        if (offlineVideoSupported()) {
          this.offlineVideoPlayback.ensureReady();
        }

        // Covers both "already logged in" and just-logged-in — every login flow ends up back
        // through this component.
        //
        // The sweep runs ONLY on a refresh that actually reached the server. An
        // already-expired session is now deliberately NOT swept here (the line this replaces
        // claimed the opposite, and was the behaviour being fixed): expiry is not evidence
        // that anything was revoked, and treating it as such deleted the student's downloads. refresh()
        // swallows its own errors and always resolves, so an unconditional .then() here ran
        // the sweep after failed refreshes too — which is how it came to delete downloads on
        // every offline startup. See OfflineEntitlementService.sweepIfExpired.
        this.offlineEntitlement.refresh().then((refreshed) => {
          if (refreshed) this.offlineEntitlement.sweepIfExpired();
          this.offlineEntitlement.refreshEnrolledModuleSummaries();
        });
      }
    } catch (err) {
      console.error('[offline] session init failed', err);
      this.offlineFeatureOn = false;
    }
  }

  async ngOnInit() {
    //we don't want the application's default styles to display while we are loading the customer syles
    //so to prevent that, we hide the entire application until the styles have been retreived and the css FULLY loaded
    //to know if they are fully loaded, we may have to check if onload has fired on all the link tags
    try {
      this.initAppUpdates();
      // NOT awaited, and never to be awaited again. This used to be the first statement in
      // startup with an `await` on it, which meant one IndexedDB request that never settled
      // held the entire app before a single pixel rendered — notes included, online and off.
      // The .catch() was no defence: a promise that never settles never rejects either.
      // Nothing later in startup depends on the result, so it runs alongside.
      this.checkOfflineDataFormat();
      this.checkForSavedContent();
      this.locationService.getLocation();
      //get online status
      this.updateOnlineStatus();
      window.addEventListener('online', this.updateOnlineStatus.bind(this));
      window.addEventListener('offline', this.updateOnlineStatus.bind(this));
      this.offlineConnectivity.state$.subscribe((state) => (this.connState = state));
      this.utilservice.schoolInfoBundle = await this.loadSchoolInfoBundle();
      this.isSchoolEnabled = this.utilservice.schoolInfoBundle.schoolInfo.enabled;
      await this.initOfflineSession();
      if (await this.utilservice.hasFeature(FeatureGroups.SITE_CUSTOMISATION)) {
        // Contained on purpose. Both network calls behind preloadCustomStyles() have no
        // cached fallback, and getSchoolFromSubdomain() swallows its own error and returns
        // undefined — so offline, reading .id off it throws a TypeError. Uncontained, that
        // threw out of ngOnInit into the catch below and set startupFailed, which hides the
        // router outlet: at any school with this feature on, going offline blanked the
        // downloads hub, the saved-lesson reader AND the /media iframe a saved note embeds
        // its video in, all at once. Custom stylesheets are cosmetic — same reasoning as
        // customCssErrored() below, which already carries on without a file it cannot load.
        try {
          await this.preloadCustomStyles();
          this.setActiveLayoutFiles(this.router.url);

          await this.waitForAllStyleFilesToBeLoaded();
        } catch (error) {
          console.error("Could not load this school's custom styles — continuing without them", error);
        }

        this.showContent();
      }
      this.logoUrl = this.utilservice.schoolInfoBundle.schoolInfo.logoImg;
    } catch (error) {
      console.error('Application startup failed', error);
      // 404 means the subdomain resolved to no school. Anything else — 500, timeout,
      // DNS, CORS — is an unexpected failure and previously fell through this catch
      // silently, leaving the app hidden forever.
      this.isNotFoundError = error?.status === 404;
      this.startupFailed = !this.isNotFoundError;
    } finally {
      // Both of these must run on every path. onInitDone releases the wait in
      // routerEventHandler, and showContent lifts the visibility gate that
      // hideContent() set in the constructor.
      this.onInitDone = true;
      this.showContent();
    }
  }

  // Fire-and-forget housekeeping. Deliberately not part of the startup chain: offline data is
  // only read later, by pages the student has to navigate to, and none of it is worth delaying
  // first paint for.
  // Fire-and-forget, like checkOfflineDataFormat below: if IndexedDB is slow or unavailable
  // this simply stays false, and the route-based escape hatch in the template covers that case
  // instead. Nothing in startup waits on it.
  private checkForSavedContent(): void {
    const school = getCurrentSchoolSubdomain();
    const user = getCurrentUserId(this.useraccessservice.getCurrentUserToken());
    if (!school || !user) return;
    this.offlineDb
      .listIndexEntries(school, user)
      .then((entries) => {
        this.hasSavedContentOnDevice = entries.some((e) => e.status === 'saved');
      })
      .catch((err) => console.error('[offline] could not check for saved content', err));
  }

  private checkOfflineDataFormat(): void {
    this.offlineDb
      .ensureDataFormatCurrent()
      .then((wiped) => {
        // The stored device key went with it. Without this, the in-memory key outlives the
        // record it came from and silently encrypts content nothing can ever decrypt.
        if (wiped) this.offlineCrypto.resetKeyCache();
      })
      .catch((err) => console.error('[offline] data-format check failed', err));
  }

  // a deliberate user action, so a full reload is appropriate here — unlike the
  // automatic reload that used to fire on a stylesheet error
  retryStartup() {
    document.location.reload();
  }

  hideContent() {
    const appRoot: any = document.getElementsByTagName('app-root')[0];
    appRoot.style.visibility = 'hidden';
    document.body.classList.add('body-loading');
  }

  showContent() {
    const appRoot: any = document.getElementsByTagName('app-root')[0];
    appRoot.style.visibility = 'visible';
    document.body.classList.remove('body-loading');
  }

  customCssLoaded(index: number) {
    console.log('loaded: ' + index);
    this.layoutFiles[index].loaded = true;
  }

  customCssErrored(index: number) {
    // Mark it loaded so the wait below can finish, and carry on without this file.
    // This used to alert() and reload, which looped forever when a school's custom
    // stylesheet URL was permanently broken — locking them out of the settings page
    // they needed to fix it.
    console.error(
      `Could not load custom css file '${this.layoutFiles[index].filePath}' — continuing without it`
    );
    this.layoutFiles[index].loaded = true;
  }

  checkAllStyleFilesLoaded() {
    for (let i = 0; i < this.layoutFiles.length; i++) {
      if (!this.layoutFiles[i].loaded) {
        return false;
      }
    }
    return true;
  }

  // A stylesheet that fires neither load nor error would otherwise hang the app
  // behind the visibility gate indefinitely, so give up after a deadline and show
  // the page with whatever styles did arrive.
  waitForAllStyleFilesToBeLoaded(timeoutMs = 10000): Promise<void> {
    return new Promise((accept) => {
      const startedAt = Date.now();
      const check = () => {
        if (this.checkAllStyleFilesLoaded()) {
          accept();
        } else if (Date.now() - startedAt >= timeoutMs) {
          console.warn('Timed out waiting for custom stylesheets — continuing.');
          accept();
        } else {
          setTimeout(check, 200);
        }
      };
      check();
    });
  }

  // NOTHING IN THIS APP EVER ACTIVATED A SERVICE WORKER UPDATE BEFORE THIS.
  // ngsw was registered (app.module.ts) but SwUpdate was never injected anywhere, so a client
  // kept serving the bundle it first loaded, for as long as that client lived. A new deploy
  // was fetched into the cache and then simply never used. On a laptop that mostly hides
  // itself, because closing the tab ends the client and the next visit picks up the new
  // version — which is why clearing site data "fixed" things there. An installed PWA on a
  // phone is the opposite: it is resumed, not cold-started, so it can go on running a bundle
  // from several deploys ago indefinitely. That is one app running old code, not a different
  // code path for PWAs.
  private updatePending = false;

  private initAppUpdates(): void {
    if (!this.swUpdate.isEnabled) return; // dev builds (enabled: !isDevMode())

    this.swUpdate.versionUpdates.subscribe((evt) => {
      if (evt.type === 'VERSION_READY') {
        this.updatePending = true;
        this.applyPendingUpdate();
      }
    });

    // The check that actually matters for an installed app. ngsw looks for a new version when
    // a client starts; a phone PWA resumes instead of starting, so without this it may not
    // look for days. Fires on resume and on tab focus.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      this.swUpdate.checkForUpdate().catch((err) => console.error('[update] check failed', err));
    });
  }

  private applyPendingUpdate(): void {
    if (!this.updatePending) return;
    // Never reload out from under an assessment: the answers live in component state and are
    // not recoverable. routerEventHandler calls this again on the way out of that route.
    if (this.isOnAssessmentRoute) return;
    this.updatePending = false;
    // activateUpdate() must be followed by a reload, not left to take effect on its own — the
    // running app would otherwise start requesting lazy chunks that only exist in the version
    // it is no longer on.
    this.swUpdate
      .activateUpdate()
      .then(() => document.location.reload())
      .catch((err) => console.error('[update] activation failed', err));
  }

  async routerEventHandler(event: Event) {
    if (event instanceof NavigationStart) {
      console.log('router event', event);

      //hide the content of the page until the proper css has been applied
      this.hideContent();
    } else if (event instanceof NavigationEnd) {
      const navigationEnd = event as NavigationEnd;

      // Hide progress spinner or progress bar
      //this.currentRoute = event.url;
      console.log('router event', event);
      this.isOnAssessmentRoute = ASSESSMENT_ROUTE_PATHS.some((path) => navigationEnd.urlAfterRedirects.includes(`/${path}`));
      // A version that arrived while an assessment was open is applied on the way out.
      this.applyPendingUpdate();

      //apply the proper css, then display the page
      const initDonePromise = new Promise<void>((accept, reject) => {
        const initDone = () => {
          if (this.onInitDone) {
            accept();
          } else {
            setTimeout(initDone, 200);
          }
        };
        initDone();
      });

      await initDonePromise;

      this.setActiveLayoutFiles(navigationEnd.urlAfterRedirects);

      await this.waitForAllStyleFilesToBeLoaded();

      this.showContent();
    } else if (event instanceof NavigationError) {
      this.showContent();
    }
  }

  private setActiveLayoutFiles(url: string) {
    this.activeLayoutFiles = [];
    for (const layoutFile of this.layoutFiles) {
      // Stylesheets only. Uploaded layouts can now also be HTML, which a page fetches and
      // renders itself — attaching one to a <link rel="stylesheet"> would download it for
      // nothing and make the page wait on it before showing anything.
      if (!this.isStylesheet(layoutFile)) {
        continue;
      }
      try {
        if (new RegExp(layoutFile.urlRegex).test(url)) {
          this.activeLayoutFiles.push(layoutFile);
        }
      }
      catch {
        // a school can type anything into the pattern box; a bad one must not stop the app
      }
    }
  }

  private isStylesheet(layoutFile: any): boolean {
    const ext = (layoutFile?.ext ?? layoutFile?.Ext ?? '').toLowerCase();
    // fall back to the file name for records saved before the extension was recorded
    return ext ? ext === '.css' : !!layoutFile?.fileName?.toLowerCase().endsWith('.css');
  }


  private updateOnlineStatus(): void {
    this.isOnline = window.navigator.onLine;
  }

}

export class MediaBlot extends BlockEmbed {
  static blotName;
  static tagName;
  static create(value) {
    //debugger;
    const node = super.create();

    node.setAttribute('src', value);
    //node.setAttribute('frameborder', '0');
    // node.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture');
    // node.setAttribute('allowtransparency', true);
    node.setAttribute('allowfullscreen', true);
    // node.setAttribute('scrolling', '0');
    //$(node).css('width', '100%');
    //node.setAttribute('height', '315px');

    node.setAttribute('class', 'mediaiframe');

    return node;
  }

  static value(node) {
    return node.dataset.id;
  }
}
